// ============================================
// Event Therapists Management
// ============================================

import React from 'react';
import { collection, onSnapshot, doc, deleteDoc, updateDoc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Event, EventTherapist, STATUS_COLORS, TherapistAttendance } from '../../../types';

// Get today's date in YYYY-MM-DD format
const getTodayDateId = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

interface Props {
  event: Event;
}

export default function EventTherapists({ event }: Props) {
  const [therapists, setTherapists] = React.useState<EventTherapist[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'events', event.id, 'therapists'),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          uid: doc.id,
          ...doc.data(),
        })) as EventTherapist[];
        setTherapists(data);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [event.id]);

  const removeTherapist = async (uid: string) => {
    if (!window.confirm('Remove this therapist from the event?')) return;
    await deleteDoc(doc(db, 'events', event.id, 'therapists', uid));
  };

  // Admin check out a therapist
  const checkOutTherapist = async (therapist: EventTherapist) => {
    if (!window.confirm(`Check out ${therapist.displayName}?`)) return;

    try {
      const therapistRef = doc(db, 'events', event.id, 'therapists', therapist.uid);
      const todayId = getTodayDateId();
      const attendanceRef = doc(db, 'events', event.id, 'therapists', therapist.uid, 'attendance', todayId);

      // Update therapist status
      await updateDoc(therapistRef, {
        dutyStatus: 'off_duty',
        uiStatus: 'grey',
        checkedOutAt: serverTimestamp(),
        breakStartedAt: null,
      });

      // Update attendance record if exists
      const attendanceDoc = await getDoc(attendanceRef);
      if (attendanceDoc.exists()) {
        const data = attendanceDoc.data() as TherapistAttendance;
        if (!data.checkedOutAt) {
          // Calculate venue minutes
          const checkedInAt = data.checkedInAt?.toDate?.() || new Date(data.checkedInAt as any);
          const now = new Date();
          const currentSegmentMinutes = Math.round((now.getTime() - checkedInAt.getTime()) / 60000);

          let totalMinutes = currentSegmentMinutes;
          if (data.segments) {
            data.segments.forEach((seg) => {
              if (seg.minutes) {
                totalMinutes += seg.minutes;
              }
            });
          }

          await setDoc(attendanceRef, {
            checkedOutAt: serverTimestamp(),
            venueMinutes: totalMinutes,
          }, { merge: true });
        }
      }
    } catch (err) {
      console.error('Error checking out therapist:', err);
      alert('Failed to check out therapist');
    }
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return '-';
    return timestamp.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div className="loading">Loading therapists...</div>;
  }

  return (
    <div className="event-therapists">
      <div className="section-header">
        <h2>Therapists ({therapists.length})</h2>
      </div>

      {therapists.length === 0 ? (
        <div className="empty-state">
          <p>No therapists have joined this event yet.</p>
          <p>Share an invite code to let therapists join.</p>
        </div>
      ) : (
        <div className="therapists-table">
          <table>
            <thead>
              <tr>
                <th>Photo</th>
                <th>Name</th>
                <th>Status</th>
                <th>Checked In</th>
                <th>Checked Out</th>
                <th>Last Heartbeat</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {therapists.map((therapist) => (
                <tr key={therapist.uid}>
                  <td>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      {therapist.photoUrl && therapist.photoStatus === 'approved' ? (
                        <img
                          src={therapist.thumbnailUrl || therapist.photoUrl}
                          alt={therapist.displayName}
                          className="therapist-photo-small"
                        />
                      ) : (
                        <div className="therapist-photo-small placeholder">
                          {therapist.displayName.charAt(0)}
                        </div>
                      )}
                      {therapist.photoStatus === 'pending' && (
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: '#f59e0b',
                          color: '#fff',
                          fontSize: '0.5rem',
                          textAlign: 'center',
                          borderRadius: '0 0 4px 4px',
                        }}>
                          PENDING
                        </div>
                      )}
                    </div>
                  </td>
                  <td>{therapist.displayName}</td>
                  <td>
                    <span
                      style={{
                        fontWeight: 600,
                        color: STATUS_COLORS[therapist.uiStatus],
                        textTransform: 'capitalize',
                      }}
                    >
                      {therapist.dutyStatus.replace('_', ' ')}
                    </span>
                  </td>
                  <td>{formatTime(therapist.checkedInAt)}</td>
                  <td>{formatTime(therapist.checkedOutAt)}</td>
                  <td>{formatTime(therapist.lastHeartbeatAt)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {/* Show Check Out button if therapist is on duty or on break */}
                      {(therapist.dutyStatus === 'on_duty' || therapist.dutyStatus === 'break') && (
                        <button
                          onClick={() => checkOutTherapist(therapist)}
                          className="btn btn-sm"
                          style={{ backgroundColor: '#f59e0b', color: '#fff' }}
                        >
                          Check Out
                        </button>
                      )}
                      <button
                        onClick={() => removeTherapist(therapist.uid)}
                        className="btn btn-sm btn-danger"
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
